// frontend/src/components/p2p/OrderBookTabs.tsx
import { useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { ArrowRight, Clock, Loader2, Zap } from 'lucide-react'
import { formatUnits } from 'viem'
import toast from 'react-hot-toast'
import { P2POrder, apiClient } from '../../lib/api'
import { useMyCompatibleOrders } from '../../hooks/useMyCompatibleOrders'
import { useP2POrderbook } from '../../hooks/useP2POrderbook'
import { useCreateOrder } from '../../hooks/useCreateOrder'
import {
  P2P_PAIR,
  infoForSide,
  infoForOppositeSide,
} from '../../lib/p2p/tokens'
import { InfoTooltip } from './InfoTooltip'
import { OrderFlowTerminal } from './OrderFlowTerminal'
import { cn } from '../../lib/utils'

type Tab = 'sell-w2' | 'sell-usdt'

/**
 * Main orderbook view: tabs select which side of the market you're
 * looking at. Each row shows what the maker offers, what you'd give,
 * what you'd get, and a Tomar button to execute against that order.
 */
export function OrderBookTabs() {
  const { orders, connected } = useP2POrderbook(P2P_PAIR)
  const [tab, setTab] = useState<Tab>('sell-w2')

  const sellW2 = useMemo(() => sortAsks(orders, 'SELL_W2USDT'), [orders])
  const sellUsdt = useMemo(() => sortAsks(orders, 'SELL_USDT'), [orders])

  const visible = tab === 'sell-w2' ? sellW2 : sellUsdt

  return (
    <section className="rounded-2xl border border-dark-800 bg-dark-900/40 overflow-hidden flex flex-col">
      {/* Tabs header */}
      <div className="flex items-stretch border-b border-dark-800">
        <TabButton active={tab === 'sell-w2'} onClick={() => setTab('sell-w2')} count={sellW2.length}>
          <span className="text-rose-400">Venden w2usdt</span>
        </TabButton>
        <TabButton active={tab === 'sell-usdt'} onClick={() => setTab('sell-usdt')} count={sellUsdt.length}>
          <span className="text-emerald-400">Venden USDT</span>
        </TabButton>
        <div className="flex-1 flex items-center justify-end px-4">
          <div className={cn('flex items-center gap-1.5 text-[11px] font-mono', connected ? 'text-uni-400' : 'text-amber-400')}>
            <span className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-uni-500 animate-pulse' : 'bg-amber-400')} />
            {connected ? 'LIVE' : 'OFFLINE'}
          </div>
        </div>
      </div>

      {/* Helper text */}
      <div className="px-4 py-2.5 border-b border-dark-800 bg-dark-900/30 text-[11px] text-dark-400">
        {tab === 'sell-w2' ? (
          <>
            Quienes posean <span className="text-rose-300">w2usdt</span> y quieran <span className="text-emerald-300">USDT</span>. Si tomas una, pagas con USDT y recibes w2usdt en Uni2.
            <InfoTooltip>
              <strong className="text-uni-300">Tomar</strong>: postea automáticamente una orden contraria al precio del maker. El matcher las cruza y la liquidación on-chain ocurre en ambas chains en segundos.
            </InfoTooltip>
          </>
        ) : (
          <>
            Quienes posean <span className="text-emerald-300">USDT</span> y quieran <span className="text-rose-300">w2usdt</span>. Si tomas una, pagas con w2usdt y recibes USDT en BSC.
            <InfoTooltip>
              <strong className="text-uni-300">Tomar</strong>: postea automáticamente una orden contraria al precio del maker. El matcher las cruza y la liquidación on-chain ocurre en ambas chains en segundos.
            </InfoTooltip>
          </>
        )}
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <EmptyTab side={tab} />
      ) : (
        <div className="divide-y divide-dark-800/40">
          {visible.map((order) => (
            <OrderRowItem key={order.orderId} order={order} />
          ))}
        </div>
      )}
    </section>
  )
}

function sortAsks(orders: P2POrder[], side: 'SELL_W2USDT' | 'SELL_USDT'): P2POrder[] {
  return orders
    .filter((o) => o.side === side)
    .filter((o) => o.status === 'ACTIVE' || o.status === 'PARTIAL')
    .filter((o) => BigInt(o.amount) - BigInt(o.filled) > 0n)
    .filter((o) => new Date(o.expiresAt).getTime() > Date.now())
    .sort((a, b) => a.premiumBps - b.premiumBps)
    .slice(0, 50)
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean
  onClick: () => void
  count: number
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-uni-500 text-white bg-dark-900/40'
          : 'border-transparent text-dark-400 hover:text-white',
      )}
    >
      {children}
      <span className={cn(
        'text-[10px] font-mono px-1.5 py-0.5 rounded',
        active ? 'bg-uni-500/20 text-uni-300' : 'bg-dark-800 text-dark-500',
      )}>
        {count}
      </span>
    </button>
  )
}

function EmptyTab(_props: { side: Tab }) {
  return (
    <div className="px-4 py-16 text-center space-y-2">
      <div className="text-3xl text-dark-700">∅</div>
      <p className="text-sm text-dark-400">No hay órdenes activas en este lado</p>
      <p className="text-[11px] text-dark-500">
        Crea la primera desde el panel de la izquierda — alguien la verá y la podrá tomar.
      </p>
    </div>
  )
}

function OrderRowItem({ order }: { order: P2POrder }) {
  const { address, isConnected } = useAccount()
  const { createOrder, stage, steps, error, txHash, reset } = useCreateOrder()
  const [taking, setTaking] = useState(false)
  const [terminalOpen, setTerminalOpen] = useState(false)

  const compatibles = useMyCompatibleOrders(order)
  const [crossOpen, setCrossOpen] = useState(false)
  const [crossing, setCrossing] = useState(false)
  const [pendingConfirm, setPendingConfirm] = useState<null | {
    myOrderId: string;
    myPremiumBps: number;
    targetPremiumBps: number;
    deltaBps: number;
  }>(null)

  const fromInfo = infoForSide(order.side)       // what maker locked
  const toInfo = infoForOppositeSide(order.side) // what maker wants
  const remaining = BigInt(order.amount) - BigInt(order.filled)

  // Mirror the on-chain formula in P2PEscrowV2._computeExpected so a maker's
  // negative premium translates into the taker paying less, not more.
  const iWouldGive = (remaining * BigInt(10000 + order.premiumBps)) / 10000n

  const isMyOrder = address?.toLowerCase() === order.maker.toLowerCase()
  const expiresMs = new Date(order.expiresAt).getTime() - Date.now()

  async function take() {
    if (!address) return
    setTaking(true)
    setTerminalOpen(true)
    try {
      // expiresAt is informational here — the backend forces a short
      // window (60s) when it sees targetMakerOrderId. fillMode is also
      // forced to PARTIAL_OK on the backend for the same reason.
      await createOrder({
        side: order.side === 'SELL_W2USDT' ? 'SELL_USDT' : 'SELL_W2USDT',
        amount: iWouldGive.toString(),
        premiumBps: order.premiumBps,
        expiresAt: Math.floor(Date.now() / 1000) + 120,
        fillMode: 'PARTIAL_OK',
        targetMakerOrderId: order.orderId,
        takerAddress: address,
      })
    } finally {
      setTaking(false)
    }
  }

  async function startCross(myOrderId: string, confirm = false) {
    if (!address) return
    setCrossing(true)
    if (!confirm) setPendingConfirm(null)
    try {
      const res = await apiClient.crossP2PExistingOrders({
        myOrderId,
        targetOrderId: order.orderId,
        address,
        confirmLargeDelta: confirm,
      })
      if ('requiresConfirmation' in res) {
        setPendingConfirm({
          myOrderId,
          myPremiumBps: res.myPremiumBps,
          targetPremiumBps: res.targetPremiumBps,
          deltaBps: res.deltaBps,
        })
      } else {
        setPendingConfirm(null)
        setCrossOpen(false)
        const sign = res.premiumAppliedBps >= 0 ? '+' : '';
        toast.success(`Cruce encolado a ${sign}${(res.premiumAppliedBps / 100).toFixed(2)}%`);
      }
    } catch (err: any) {
      console.error('cross failed', err)
      toast.error(err?.message ?? 'Cross failed');
    } finally {
      setCrossing(false)
    }
  }

  function closeTerminal() {
    setTerminalOpen(false)
    reset()
  }

  const busy = taking || (stage !== 'idle' && stage !== 'success' && stage !== 'error')
  const premiumPct = (order.premiumBps / 100).toFixed(2)

  return (
    <div className="px-3 sm:px-4 py-3 hover:bg-dark-800/30 transition-colors">
      {/* Layout: on <sm we stack into 2 rows (premium+amounts; expires+action);
          on sm+ everything fits in a single row like the original design. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap">
        {/* Premium badge */}
        <div className={cn(
          'shrink-0 px-2 py-1 rounded-md text-xs font-mono border min-w-[60px] text-center',
          order.premiumBps >= 0
            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
            : 'bg-rose-500/10 text-rose-300 border-rose-500/30',
        )}>
          {order.premiumBps >= 0 ? '+' : ''}{premiumPct}%
        </div>

        {/* Amount + cross-token preview */}
        <div className="min-w-0 flex-1 grid grid-cols-2 gap-2 sm:gap-3 items-center order-3 sm:order-none w-full sm:w-auto">
          <div className="min-w-0">
            <div className="text-[10px] font-mono text-dark-500 uppercase tracking-wider">Ofrecen</div>
            <div className="font-mono text-sm text-white tabular-nums truncate">
              {humanAmount(remaining, fromInfo.decimals)} {fromInfo.tokenSymbol}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-mono text-dark-500 uppercase tracking-wider flex items-center gap-1">
              <ArrowRight className="w-2.5 h-2.5" />
              Tú das
            </div>
            <div className="font-mono text-sm text-dark-200 tabular-nums truncate">
              {humanAmount(iWouldGive, toInfo.decimals)} {toInfo.tokenSymbol}
            </div>
          </div>
        </div>

        {/* Expires — hidden on small screens to keep the row compact */}
        <div className="hidden sm:flex items-center gap-1 text-[10px] font-mono text-dark-500 shrink-0">
          <Clock className="w-3 h-3" />
          {formatDuration(Math.max(0, expiresMs))}
        </div>

        {/* Action — pushed to the right on mobile too */}
        <div className="ml-auto sm:ml-0 shrink-0">
          {isMyOrder ? (
            <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 px-2 py-1 rounded border border-purple-500/30 bg-purple-500/5">
              Tuya
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={take}
                disabled={!isConnected || busy}
                className={cn(
                  'px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5',
                  isConnected && !busy
                    ? 'border-uni-500/40 bg-uni-500/10 text-uni-400 hover:bg-uni-500/20 hover:border-uni-500/60 hover:shadow-glow-sm'
                    : 'border-dark-700 bg-dark-800/50 text-dark-500 cursor-not-allowed',
                )}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Tomar
              </button>
              {compatibles.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCrossOpen(true)}
                  disabled={!isConnected || crossing}
                  className={cn(
                    'ml-2 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5',
                    isConnected && !crossing
                      ? 'border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
                      : 'border-dark-700 bg-dark-800/50 text-dark-500 cursor-not-allowed',
                  )}
                  title={`Tomar a ${order.premiumBps >= 0 ? '+' : ''}${(order.premiumBps / 100).toFixed(2)}% con mi orden`}
                >
                  Con mi orden
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <OrderFlowTerminal
        open={terminalOpen}
        stage={stage}
        steps={steps}
        txHash={txHash}
        error={error}
        onClose={closeTerminal}
        title="Tomando orden"
      />

      {crossOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
          onClick={() => !crossing && setCrossOpen(false)}
        >
          <div
            className="bg-dark-900 border border-dark-700 rounded-lg p-5 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {!pendingConfirm ? (
              <>
                <h3 className="text-white text-sm font-medium mb-3">Tomar con mi orden</h3>
                <p className="text-xs text-dark-400 mb-4">
                  Cruza una de tus órdenes existentes con esta. Sin firma de wallet, sin tx — el relayer settle ambas legs.
                </p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {compatibles.map((c) => {
                    const sign = c.deltaBps >= 0 ? '+' : '';
                    const cls =
                      c.deltaBps === 0
                        ? 'text-dark-300'
                        : c.deltaBps > 0
                        ? 'text-rose-300'
                        : 'text-emerald-300';
                    return (
                      <button
                        key={c.order.orderId}
                        onClick={() => startCross(c.order.orderId)}
                        disabled={crossing}
                        className="w-full text-left p-3 rounded border border-dark-700 hover:border-purple-500/50 hover:bg-dark-800/50 transition-all flex flex-col gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white">
                            Tomar a {order.premiumBps >= 0 ? '+' : ''}{(order.premiumBps / 100).toFixed(2)}%
                          </span>
                          <span className={cn('font-mono', cls)}>
                            {sign}{(c.deltaBps / 100).toFixed(2)}% vs tu orden
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-dark-500">
                          Tu orden: {c.order.premiumBps >= 0 ? '+' : ''}{(c.order.premiumBps / 100).toFixed(2)}% · restante: {c.remaining.toString()}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCrossOpen(false)}
                  disabled={crossing}
                  className="mt-4 text-xs text-dark-400 hover:text-white"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <h3 className="text-white text-sm font-medium mb-3">Confirmar precio</h3>
                <p className="text-xs text-dark-300 mb-4">
                  Vas a aceptar un premium de{' '}
                  <span className="text-white font-mono">
                    {pendingConfirm.targetPremiumBps >= 0 ? '+' : ''}
                    {(pendingConfirm.targetPremiumBps / 100).toFixed(2)}%
                  </span>{' '}
                  ({pendingConfirm.deltaBps >= 0 ? '+' : ''}
                  {(pendingConfirm.deltaBps / 100).toFixed(2)}%{' '}
                  {pendingConfirm.deltaBps > 0 ? 'peor' : 'mejor'} que tu orden a{' '}
                  <span className="text-white font-mono">
                    {pendingConfirm.myPremiumBps >= 0 ? '+' : ''}
                    {(pendingConfirm.myPremiumBps / 100).toFixed(2)}%
                  </span>).
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setPendingConfirm(null)}
                    className="px-3 py-1.5 text-xs text-dark-300 hover:text-white"
                  >
                    Atrás
                  </button>
                  <button
                    onClick={() => startCross(pendingConfirm.myOrderId, true)}
                    disabled={crossing}
                    className="px-3 py-1.5 text-xs rounded-md bg-purple-500/20 text-purple-300 border border-purple-500/40 hover:bg-purple-500/30 disabled:opacity-50"
                  >
                    Confirmar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function humanAmount(wei: bigint, decimals: number, maxFrac = 4): string {
  try {
    const s = formatUnits(wei, decimals)
    const [w, f = ''] = s.split('.')
    const grouped = w.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const frac = f.slice(0, maxFrac).replace(/0+$/, '')
    return frac ? `${grouped}.${frac}` : grouped
  } catch {
    return '0'
  }
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}
